import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { KanbanColumn } from './KanbanColumn';
import { CandidateCard } from './CandidateCard';
import { usePatchCandidateStatus } from '@/hooks/useCandidates';
import type { Candidate, FunnelStatus } from '@/types';

const COLUMNS: { id: FunnelStatus; title: string }[] = [
  { id: 'prospeccao', title: 'Prospeccao' },
  { id: 'r1', title: 'R1 - Cafe' },
  { id: 'r2_r3', title: 'R2/R3 - Entrevista' },
  { id: 'proposta', title: 'Proposta' },
  { id: 'fechado_ganho', title: 'Fechado - Ganho' },
  { id: 'fechado_perdido', title: 'Fechado - Perdido' },
];

interface Props {
  candidates: Candidate[];
  onCardClick: (candidate: Candidate) => void;
}

const VALID_STATUSES: FunnelStatus[] = [
  'prospeccao', 'r1', 'r2_r3', 'proposta', 'fechado_ganho', 'fechado_perdido',
];

export function KanbanBoard({ candidates, onCardClick }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const patchStatus = usePatchCandidateStatus();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const activeCandidate = activeId
    ? candidates.find((c) => c.id === activeId)
    : null;

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const candidateId = String(active.id);
    const newStatus = String(over.id) as FunnelStatus;

    const candidate = candidates.find((c) => c.id === candidateId);
    if (!candidate) return;
    if (candidate.funnel_status === newStatus) return;

      if (!VALID_STATUSES.includes(newStatus)) return;

    patchStatus.mutate({ id: candidateId, status: newStatus });
  }

  const byStatus = (status: FunnelStatus) =>
    candidates.filter((c) => c.funnel_status === status);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 h-full">
        {COLUMNS.map((col) => (
          <KanbanColumn
            key={col.id}
            id={col.id}
            title={col.title}
            candidates={byStatus(col.id)}
            onCardClick={onCardClick}
            onStatusChange={(id, status) => patchStatus.mutate({ id, status })}
          />
        ))}
      </div>
      <DragOverlay>
        {activeCandidate && (
          <CandidateCard
            candidate={activeCandidate}
            onClick={() => undefined}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}
